import { useState } from 'react'
import { ActionIcon } from '@mantine/core'
import { Spotlight, spotlight, type SpotlightActionGroupData } from '@mantine/spotlight'
import { useDebouncedValue } from '@mantine/hooks'
import { IconBuildingStore, IconChecklist, IconSearch } from '@tabler/icons-react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { searchGlobal } from '../../shared/api/search'
import { TaskModal } from '../task-modal/task-modal'

const MIN_QUERY = 2

/**
 * Глобальный поиск (Cmd+K / Ctrl+K): задачи по title + проекты по имени,
 * серверный ILIKE в RBAC-границах (GET /search). Фильтрация на клиенте
 * отключена (filter=identity) — сервер уже отдал только релевантное.
 */
export function GlobalSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [debounced] = useDebouncedValue(query, 250)
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)

  const trimmed = debounced.trim()
  const q = useQuery({
    queryKey: ['search', trimmed],
    queryFn: () => searchGlobal(trimmed),
    enabled: trimmed.length >= MIN_QUERY,
    placeholderData: keepPreviousData,
  })

  const actions: SpotlightActionGroupData[] = []
  if (trimmed.length >= MIN_QUERY && q.data) {
    if (q.data.projects.length > 0) {
      actions.push({
        group: 'Проекты',
        actions: q.data.projects.map((p) => ({
          id: `project-${p.id}`,
          label: p.name,
          description: 'Открыть доску',
          leftSection: <IconBuildingStore size={18} stroke={1.6} color={p.accent_color} />,
          onClick: () => navigate(`/projects/${p.id}`),
        })),
      })
    }
    if (q.data.tasks.length > 0) {
      actions.push({
        group: 'Задачи',
        actions: q.data.tasks.map((t) => ({
          id: `task-${t.id}`,
          label: t.title,
          description: t.is_done ? `${t.project.name} · выполнена` : t.project.name,
          leftSection: <IconChecklist size={18} stroke={1.6} color={t.project.accent_color} />,
          onClick: () => setOpenTaskId(t.id),
        })),
      })
    }
  }

  return (
    <>
      <ActionIcon
        variant="subtle"
        color="gray"
        size="lg"
        onClick={spotlight.open}
        title="Поиск (⌘K)"
      >
        <IconSearch size={20} stroke={1.6} />
      </ActionIcon>

      <Spotlight
        actions={actions}
        query={query}
        onQueryChange={setQuery}
        shortcut={['mod + K']}
        filter={(_q, all) => all}
        highlightQuery
        scrollable
        maxHeight={420}
        nothingFound={
          trimmed.length < MIN_QUERY
            ? 'Введите минимум 2 символа'
            : q.isFetching
              ? 'Ищу…'
              : 'Ничего не найдено'
        }
        searchProps={{
          leftSection: <IconSearch size={18} stroke={1.6} />,
          placeholder: 'Поиск задач и проектов…',
        }}
      />

      <TaskModal taskId={openTaskId} onClose={() => setOpenTaskId(null)} onOpenTask={setOpenTaskId} />
    </>
  )
}
